/* `maxOut` is the per-model output-token cap we ask for. Use the provider's
 * documented ceiling for the model (Aug 2026 values). Bumping to the true
 * ceiling fixes silent truncation on long syntheses.
 *
 * Capability flags (Aug 2026):
 *  - vision: model accepts image inputs (PNG/JPG/WEBP/GIF) as content parts
 *  - pdf:    model accepts PDFs directly (no text extraction needed)
 *  For models that lack a flag, the dispatcher falls back to text-only.
 *
 * DEPRECATION POLICY: never delete a model from this catalog — keep the
 * entry so saved sessions and history rows that reference its id still
 * resolve to a valid label + pricing. When a provider retires the model
 * upstream, mark the family with "Legacy · ..." and leave the row. See
 * MODEL_ID_ALIASES below for automatic remapping (e.g. old chat sessions
 * that saved "claude-opus-4-7" get routed to "claude-opus-5" at call time
 * without losing their prompt / responses history). */
export const MODELS = [
  // ========== Anthropic — Claude 5 family (current flagship, 2026) ==========
  { id: "claude-opus-5",              provider: "anthropic", label: "Claude Opus 5",              family: "Frontier",  maxOut: 32000, vision: true, pdf: true },
  { id: "claude-sonnet-5",            provider: "anthropic", label: "Claude Sonnet 5",            family: "Balanced",  maxOut: 64000, vision: true, pdf: true },
  { id: "claude-fable-5",             provider: "anthropic", label: "Claude Fable 5",             family: "Creative",  maxOut: 16384, vision: true, pdf: true },
  { id: "claude-haiku-4-5-20251001",  provider: "anthropic", label: "Claude Haiku 4.5",           family: "Fast",      maxOut: 8192,  vision: true, pdf: true },

  // ========== OpenAI — GPT-4o + o1 GA (2026) ==========
  { id: "gpt-4o",          provider: "openai",    label: "GPT-4o",            family: "Frontier",  maxOut: 16384, vision: true },
  { id: "gpt-4o-mini",     provider: "openai",    label: "GPT-4o mini",       family: "Fast",      maxOut: 16384, vision: true },
  { id: "o1",              provider: "openai",    label: "o1",                family: "Reasoning", maxOut: 32768 },

  // ========== Google — Gemini 2.5 family ==========
  { id: "gemini-2.5-pro",   provider: "google",    label: "Gemini 2.5 Pro",    family: "Frontier", maxOut: 65536, vision: true, pdf: true },
  { id: "gemini-2.5-flash", provider: "google",    label: "Gemini 2.5 Flash",  family: "Balanced", maxOut: 65536, vision: true, pdf: true },
  { id: "gemini-2.0-flash", provider: "google",    label: "Gemini 2.0 Flash",  family: "Fast",     maxOut: 8192,  vision: true, pdf: true },

  // ========== xAI ==========
  { id: "grok-2-latest",   provider: "xai",       label: "Grok 2",            family: "Frontier",  maxOut: 8192,  vision: true },

  // ========== Mistral ==========
  { id: "mistral-large-latest", provider: "mistral", label: "Mistral Large",  family: "Frontier",  maxOut: 32000 },

  // ========== Groq — free tier, OpenAI-compatible, ultra-fast LPU (8K ceiling per model) ==========
  { id: "llama-3.3-70b-versatile", provider: "groq", label: "Llama 3.3 70B",  family: "Free · Fast",      maxOut: 8192 },
  { id: "llama-3.1-8b-instant",    provider: "groq", label: "Llama 3.1 8B",   family: "Free · Ultra-fast",maxOut: 8192 },
  { id: "mixtral-8x7b-32768",      provider: "groq", label: "Mixtral 8×7B",   family: "Free",             maxOut: 8192 },
  { id: "gemma2-9b-it",            provider: "groq", label: "Gemma 2 9B",     family: "Free · Light",     maxOut: 8192 },

  // ========== OpenRouter — aggregator, :free variants ==========
  { id: "deepseek/deepseek-chat:free",            provider: "openrouter", label: "DeepSeek V3 (free)",       family: "Free",             maxOut: 8192 },
  { id: "meta-llama/llama-3.3-70b-instruct:free", provider: "openrouter", label: "Llama 3.3 70B (free)",     family: "Free",             maxOut: 8192 },
  { id: "qwen/qwq-32b:free",                      provider: "openrouter", label: "QwQ 32B Reasoning (free)", family: "Free · Reasoning", maxOut: 32768 },

  // ========== DeepSeek — paid, ~10× cheaper than frontier ==========
  { id: "deepseek-chat",     provider: "deepseek", label: "DeepSeek V3",  family: "Frontier",  maxOut: 8192  },
  { id: "deepseek-reasoner", provider: "deepseek", label: "DeepSeek R1",  family: "Reasoning", maxOut: 65536 },

  // ========== Legacy — kept for session/history back-compat, hidden by default in the picker.
  //           New selections use the Claude 5 family + o1 GA above. ==========
  { id: "claude-opus-4-7",   provider: "anthropic", label: "Claude Opus 4.7 (legacy)",   family: "Legacy · Frontier",  maxOut: 32000, vision: true, pdf: true, deprecated: true },
  { id: "claude-sonnet-4-6", provider: "anthropic", label: "Claude Sonnet 4.6 (legacy)", family: "Legacy · Balanced",  maxOut: 64000, vision: true, pdf: true, deprecated: true },
  { id: "claude-haiku-4-5",  provider: "anthropic", label: "Claude Haiku 4.5 (aliased)", family: "Legacy · Fast",      maxOut: 8192,  vision: true, pdf: true, deprecated: true },
  { id: "o1-preview",        provider: "openai",    label: "o1 preview (legacy)",        family: "Legacy · Reasoning", maxOut: 32768, deprecated: true },
];

/* MODEL_ID_ALIASES — when a saved session, history row, or autosave blob
 * references a retired id, resolveModelId(oldId) returns the successor so
 * the app can still price / label / dispatch it correctly. Add an entry
 * here EVERY TIME you retire a model id. The legacy entries above stay in
 * MODELS so the label rendering path (which uses MODELS.find) doesn't need
 * to know about aliases — but callers that dispatch via callModel should
 * use resolveModelId first to route the request to the current model. */
export const MODEL_ID_ALIASES: Record<string, string> = {
  "claude-opus-4-7":   "claude-opus-5",
  "claude-sonnet-4-6": "claude-sonnet-5",
  "claude-haiku-4-5":  "claude-haiku-4-5-20251001",
  "o1-preview":        "o1",
};

/** Resolve a possibly-deprecated model id to the current canonical id. */
export function resolveModelId(id: string): string {
  return MODEL_ID_ALIASES[id] || id;
}

/* Floor used when a model is missing its `maxOut` (e.g. a custom add).
 * 8192 is the universal floor across every adapter — safe but conservative. */
export const DEFAULT_MAX_OUT = 8192;

/* Provider colors used by the 3D constellation (hex) and CSS contexts (hex string). */
export const PROVIDER_COLORS_HEX = {
  openai: 0x10a37f, anthropic: 0xd97757, google: 0x4285f4,
  xai: 0xe8e8e8, mistral: 0xff7000,
  groq: 0xF97316, openrouter: 0x8B5CF6, deepseek: 0x4D6BFE,
};
export const PROVIDER_COLORS_CSS = {
  openai: "#10a37f", anthropic: "#d97757", google: "#4285f4",
  xai: "#e8e8e8", mistral: "#ff7000",
  groq: "#F97316", openrouter: "#8B5CF6", deepseek: "#4D6BFE",
};

// USD per 1M tokens, knowledge cutoff Aug 2026 — keep these synced with
// the providers' public price pages. Update if a model gets repriced.
// Legacy entries kept so cost totals on saved sessions stay correct.
export const MODEL_PRICING = {
  // ===== Anthropic — Claude 5 family (2026 flagship) =====
  "claude-opus-5":              { in: 15.00, out: 75.00 },   // Opus tier
  "claude-sonnet-5":            { in:  3.00, out: 15.00 },   // Sonnet tier
  "claude-fable-5":             { in:  5.00, out: 25.00 },   // Fable tier (creative, est.)
  "claude-haiku-4-5-20251001":  { in:  0.80, out:  4.00 },   // Haiku tier
  // ===== OpenAI =====
  "gpt-4o":               { in: 2.50,  out: 10.00 },
  "gpt-4o-mini":          { in: 0.15,  out: 0.60  },
  "o1":                   { in: 15.00, out: 60.00 },
  // ===== Google =====
  "gemini-2.5-pro":       { in: 1.25,  out: 10.00 },
  "gemini-2.5-flash":     { in: 0.30,  out: 2.50  },
  "gemini-2.0-flash":     { in: 0.075, out: 0.30  },
  // ===== xAI + Mistral =====
  "grok-2-latest":        { in: 5.00,  out: 15.00 },
  "mistral-large-latest": { in: 2.00,  out: 6.00  },
  // ===== Groq — free tier (rate-limited, but $0) =====
  "llama-3.3-70b-versatile": { in: 0,    out: 0,    free: true },
  "llama-3.1-8b-instant":    { in: 0,    out: 0,    free: true },
  "mixtral-8x7b-32768":      { in: 0,    out: 0,    free: true },
  "gemma2-9b-it":            { in: 0,    out: 0,    free: true },
  // ===== OpenRouter — :free variants =====
  "deepseek/deepseek-chat:free":              { in: 0, out: 0, free: true },
  "meta-llama/llama-3.3-70b-instruct:free":   { in: 0, out: 0, free: true },
  "qwen/qwq-32b:free":                        { in: 0, out: 0, free: true },
  // ===== DeepSeek — paid (cheap) =====
  "deepseek-chat":     { in: 0.27, out: 1.10 },
  "deepseek-reasoner": { in: 0.55, out: 2.19 },
  // ===== Legacy — kept so cost totals on saved sessions stay accurate =====
  "claude-opus-4-7":   { in: 15.00, out: 75.00 },
  "claude-sonnet-4-6": { in:  3.00, out: 15.00 },
  "claude-haiku-4-5":  { in:  0.80, out:  4.00 },
  "o1-preview":        { in: 15.00, out: 60.00 },
};

export const PROVIDERS_BILLING_URLS = {
  OpenAI: "https://platform.openai.com/account/billing",
  Anthropic: "https://console.anthropic.com/settings/billing",
  Google: "https://aistudio.google.com/app/apikey",
  xAI: "https://console.x.ai/",
  Mistral: "https://console.mistral.ai/billing/",
};
